import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Alert, Button, Paper, Chip, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkflowRunForm from '../../components/workflow/WorkflowRunForm';
import WorkflowCanvas from '../../components/workflow/WorkflowCanvas';
import billingApi from '../../services/billingApi';
import { formatDate } from '../../utils/formatters';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';
import { isManualReview } from '../../utils/workflowStatus';

function categoryColor(cat) {
  const colors = { subscription: 'primary', billing: 'secondary', override: 'warning', bulk: 'info', custom: 'default' };
  return colors[cat] || 'default';
}

export default function WorkflowRunPage() {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Track executed instance for step status overlay on canvas
  const [executedInstance, setExecutedInstance] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({});
  // 999.4760: the recursive setTimeout poll had no ref and no unmount
  // cleanup, so a non-terminal status (or, before this fix, manual_review)
  // polled forever and survived navigation away from this page.
  const pollTimeoutRef = useRef(null);

  useEffect(() => {
    if (workflowId) loadDefinition();
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [workflowId]);

  async function loadDefinition() {
    setLoading(true);
    try {
      const data = await billingApi.get(`/api/admin/workflows/definitions/${workflowId}`);
      setDefinition(data.definition);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleExecuted(instance) {
    setExecutedInstance(instance);
    // Start polling step statuses for canvas overlay
    pollStepStatuses(instance.id);
  }

  async function pollStepStatuses(instanceId) {
    const poll = async () => {
      try {
        const data = await billingApi.get(`/api/admin/workflows/instances/${instanceId}`);
        const statuses = {};
        for (const log of (data.step_logs || [])) {
          statuses[log.step_id] = log.status;
        }
        setStepStatuses(statuses);
        setExecutedInstance(data.instance);

        // 999.4760: manual_review is TERMINAL (999.4741 — no supported
        // transition leads out of it) so it belongs in this stop-polling
        // list same as the resolved outcomes; it is handled as its own
        // state at render time (see the Chip color below), never conflated
        // with a resolved success/failure.
        if (!['completed', 'failed', 'canceled', 'undone', 'manual_review'].includes(data.instance?.status)) {
          pollTimeoutRef.current = setTimeout(poll, 2000);
        }
      } catch (err) {
        console.warn('Poll failed:', err.message);
      }
    };
    poll();
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <AdminPageSkeleton bare rows={5} />
      </Container>
    );
  }

  if (error || !definition) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Workflow not found'}</Alert>
      </Container>
    );
  }

  const steps = definition.steps || [];

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/workflows')}>
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5">{definition.name}</Typography>
            <Chip label={`v${definition.version}`} size="small" variant="outlined" />
            <Chip label={definition.category} size="small" color={categoryColor(definition.category)} />
          </Box>
          <Typography variant="body2" color="text.secondary">{definition.description}</Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left: Workflow visualization */}
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" sx={{ fontSize: '0.65rem', color: 'text.secondary', letterSpacing: '0.1em' }}>
              Workflow Steps ({steps.length})
            </Typography>
            <Box sx={{ mt: 1 }}>
              <WorkflowCanvas
                steps={steps}
                readOnly
                stepStatuses={stepStatuses}
              />
            </Box>

            {/* Execution meta */}
            {executedInstance && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Instance</Typography>
                  <Chip
                    label={executedInstance.status}
                    size="small"
                    // 999.4741/999.4760: manual_review must never read as
                    // 'running' (info) — it is terminal and no automatic
                    // retry will occur, unlike an in-progress instance.
                    color={
                      executedInstance.status === 'completed' ? 'success'
                        : executedInstance.status === 'failed' ? 'error'
                        : isManualReview(executedInstance.status) ? 'warning'
                        : 'info'
                    }
                  />
                </Box>
                <Typography variant="caption" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.7rem' }}>
                  {executedInstance.id}
                </Typography>
                {executedInstance.undo_eligible_until && executedInstance.status === 'completed' && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                    Undo available until {formatDate(executedInstance.undo_eligible_until)}
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: Form + Progress */}
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <WorkflowRunForm
              definition={definition}
              mode="admin"
              onExecuted={handleExecuted}
              showProgress
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}
